import Storefront from 'utils/storefront';
import { variantGet } from '../utils/storefront/queries';

export function parseGID(gid) {
  return gid.split('/').splice(-1)[0];
}

/**
 * Checks if a purchase should be prevented based on the items in the cart and the item to be added.
 * 
 * @param {Object} payload - The payload containing the merchandise information.
 * @param {Object} cart - The cart object containing current cart items.
 * @returns {Object} - An object indicating whether the purchase should be prevented and the item causing the prevention.
 */
export async function checkPreventPurchase(
  payload,
  cart,
  isRequestFromCart = false
) {
  const cartItems = await cart.items;
  const storefront = Storefront.getInstance();
  const merchandiseId = payload?.length
    ? payload[0].merchandiseId :
    `gid://shopify/ProductVariant${payload?.merchandiseId}`;

  if (!cartItems?.length) {
    return { preventPurchase: false, preventedItem: null };
  }

  // Query variant to add
  const variantToAdd = await storefront.request({
    query: variantGet,
    variables: { id: merchandiseId },
  });
  const variantToAddIsPrevented =
    variantToAdd?.data?.node?.product?.preventPurchaseWithOtherItems?.value === '1';

  // Query all variants in cart
  const variantsInCart = await Promise.all(
    cartItems.map(async (item) => {
      const variantId = item.variant_id;
      return storefront.request({
        query: variantGet,
        variables: { id: `gid://shopify/ProductVariant/${variantId}` },
      });
    })
  );

  const variantsInCartArePrevented = variantsInCart.some(
    (variant) => variant?.data?.node?.product?.preventPurchaseWithOtherItems?.value === '1'
  );

  // Check if the variant to add is already in the cart
  const isVariantToAddInCart = cartItems.some(
    (item) => `gid://shopify/ProductVariant/${item.variant_id}`
      === merchandiseId
  );

  if (isVariantToAddInCart) {
    return { preventPurchase: false, preventedItem: null };
  }

  // Check if the variant to add is prevented and there are other items in the cart
  if (variantToAddIsPrevented && cartItems.length > 0) {
    return {
      preventPurchase: true,
      preventedItem: variantToAdd.data.node
    };
  }

  // Check if any item in the cart prevents purchase with other items
  if (isRequestFromCart
    && variantsInCartArePrevented
    && cartItems.length > 1) {
    const preventedItem = variantsInCart.find(
      (variant) => variant.data.node.product.preventPurchaseWithOtherItems?.value === '1'
    );
    return {
      preventPurchase: true,
      preventedItem: preventedItem.data.node
    };
  }

  if (!isRequestFromCart
    && variantsInCartArePrevented
    && !isVariantToAddInCart) {
    const preventedItem = variantsInCart.find(
      (variant) => variant.data.node.product.preventPurchaseWithOtherItems?.value === '1'
    );
    return {
      preventPurchase: true,
      preventedItem: preventedItem.data.node
    };
  }

  return { preventPurchase: false, preventedItem: null };
}